/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DefenceSecurityMonitoring.module.scss';

type EventDef = {
  hi?: boolean;
  k: string;
  b: string;
  e: string;
  s: string;
  flag: string;
  r: string;
  n: string;
};

const EVENTS: EventDef[] = [
  {
    hi: true,
    k: 'Sanctions',
    b: 'Shareholder added to the BIS Entity List',
    e: 'KESTREL MACHINING LTD',
    s: 'BIS Entity List',
    flag: 'us',
    r: 'Federal Register notice',
    n: 'HK-2214887',
  },
  {
    hi: true,
    k: 'Threshold',
    b: 'Aggregate holding by listed parties reaches 50%',
    e: 'KESTREL MACHINING LTD',
    s: 'Companies Registry',
    flag: 'hk',
    r: 'NAR1 annual return',
    n: 'HK-2214887',
  },
  {
    hi: true,
    k: 'PEP',
    b: 'Director matched to a politically exposed person',
    e: 'VANTOR SYSTEMS LTD',
    s: 'PEP sources',
    flag: 'cy',
    r: 'HE32 officer filing',
    n: 'CY-HE414092',
  },
  {
    hi: true,
    k: 'Control',
    b: 'Tier-two supplier acquired by a new parent',
    e: 'ALTREN COMPOSITES GMBH',
    s: 'Handelsregister',
    flag: 'de',
    r: 'HRB change of control',
    n: 'HRB-118942',
  },
  {
    hi: true,
    k: 'Sanctions',
    b: 'Ownership crosses the OFAC 50 percent threshold',
    e: 'MERIDAX TRADING PTE',
    s: 'OFAC SDN sources',
    flag: 'us',
    r: 'Aggregation recalculated',
    n: 'SG-201933471K',
  },
  {
    k: 'Ownership',
    b: 'New beneficial owner recorded above a subcontractor',
    e: 'NORTHGATE PRECISION B.V.',
    s: 'UBO register',
    flag: 'nl',
    r: 'UBO declaration',
    n: 'KVK-64129077',
  },
  {
    k: 'PEP',
    b: 'Beneficial owner holds a state office',
    e: 'NORTHGATE PRECISION B.V.',
    s: 'PEP sources',
    flag: 'nl',
    r: 'UBO declaration',
    n: 'KVK-64129077',
  },
  {
    hi: true,
    k: 'Sanctions',
    b: 'UK ownership and control test now met',
    e: 'ALTREN COMPOSITES GMBH',
    s: 'OFSI consolidated list',
    flag: 'gb',
    r: 'Designation update',
    n: 'HRB-118942',
  },
  {
    k: 'Status',
    b: 'Distributor redomiciled to another jurisdiction',
    e: 'MERIDAX TRADING PTE',
    s: 'ACRA',
    flag: 'sg',
    r: 'Transfer of registration',
    n: 'SG-201933471K',
  },
  {
    k: 'Ownership',
    b: 'Existing counterparty incorporated three new entities',
    e: 'HALDEN GROUP LTD',
    s: 'Companies House',
    flag: 'gb',
    r: 'IN01 incorporation',
    n: 'GB-14827743',
  },
  {
    hi: true,
    k: 'Sanctions',
    b: 'Parent added to a consolidated sanctions list',
    e: 'ORVEDA CAPITAL LTD',
    s: 'Consolidated sources',
    flag: 'us',
    r: 'List update',
    n: 'CY-HE388215',
  },
  {
    k: 'PEP',
    b: 'Close associate appointed to the board',
    e: 'HALDEN GROUP LTD',
    s: 'PEP sources',
    flag: 'gb',
    r: 'AP01 appointment',
    n: 'GB-14827743',
  },
  {
    k: 'Status',
    b: 'Sub-tier entity marked dissolved',
    e: 'CANTERA FORJA SL',
    s: 'Registro Mercantil',
    flag: 'es',
    r: 'Dissolution entry',
    n: 'ES-B87421903',
  },
  {
    k: 'Ownership',
    b: 'Holding reduced below the aggregation threshold',
    e: 'SOREL AERO SAS',
    s: 'Infogreffe',
    flag: 'fr',
    r: 'Shareholding filing',
    n: 'FR-841207993',
  },
  {
    hi: true,
    k: 'Control',
    b: 'Right to appoint the board transferred',
    e: 'MERIDIAN HOLDINGS SARL',
    s: 'RCS',
    flag: 'lu',
    r: 'Statutes amendment',
    n: 'LU-B209114',
  },
  {
    hi: true,
    k: 'Sanctions',
    b: 'MEU List addition reaches a tier-two supplier',
    e: 'CANTERA FORJA SL',
    s: 'BIS MEU List',
    flag: 'us',
    r: 'Federal Register notice',
    n: 'ES-B87421903',
  },
  {
    k: 'Status',
    b: 'Officer disqualification recorded',
    e: 'VANTOR SYSTEMS LTD',
    s: 'Registrar of Companies',
    flag: 'cy',
    r: 'Disqualification entry',
    n: 'CY-HE414092',
  },
];

const FILTERS = ['all', 'Sanctions', 'PEP', 'Ownership', 'Control', 'Threshold', 'Status'];
const MAX_ROWS = 9;
const PUSH_MS = 4600;

type Row = {
  id: number;
  event: EventDef;
  born: number;
  entering: boolean;
};

const ago = (ms: number) => {
  const s = Math.floor(ms / 1000);
  if (s < 60) {
    return `${s}s`;
  }
  if (s < 3600) {
    return `${Math.floor(s / 60)}m`;
  }
  return `${Math.floor(s / 3600)}h`;
};

const formatClock = () => `${new Date().toISOString().slice(11, 19)} UTC`;

export const DefenceSecurityMonitoring = () => {
  const { t } = useInt();
  const liveRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(liveRef, { threshold: 0.2 });
  const idxRef = useRef(7);
  const pausedRef = useRef(false);

  const [rows, setRows] = useState<Row[]>(() =>
    Array.from({ length: 7 }, (_, j) => ({
      id: j,
      event: EVENTS[j % EVENTS.length],
      born: Date.now() - j * 43000,
      entering: false,
    })),
  );
  const [filter, setFilter] = useState('all');
  const [paused, setPaused] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [clock, setClock] = useState('--:--:-- UTC');

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    setClock(formatClock());
    const clockId = setInterval(() => setClock(formatClock()), 1000);

    return () => clearInterval(clockId);
  }, []);

  useEffect(() => {
    if (!revealed) {
      return undefined;
    }

    const pushId = setInterval(() => {
      if (pausedRef.current) {
        return;
      }

      const event = EVENTS[idxRef.current % EVENTS.length];
      idxRef.current += 1;
      const id = Date.now();
      setRows((prev) => [{ id, event, born: Date.now(), entering: true }, ...prev].slice(0, MAX_ROWS));
    }, PUSH_MS);

    return () => clearInterval(pushId);
  }, [revealed]);

  const toggleRow = (id: number) => setOpenId((prev) => (prev === id ? null : id));

  const visibleRows = rows.filter((row) => filter === 'all' || row.event.k === filter);

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('A supplier cleared at award can be caught by lunchtime.')}
          description={t(
            'Every entity in your supplier base stays under watch after onboarding, with changes surfaced on the day they are filed rather than at the next scheduled review.',
          )}
        />
      </div>

      <div className={styles.live} ref={liveRef}>
        <div className={styles.liveHead}>
          <span className={styles.ledDot} />
          <span className={styles.liveTitle}>{t('Supplier base watch')}</span>
          <span className={styles.liveSub}>2,418 entities &middot; 41 jurisdictions</span>
          <span className={styles.clock}>{clock}</span>
        </div>

        <div className={styles.controls}>
          {FILTERS.map((f) => (
            <button
              type="button"
              key={f}
              className={styles.filterBtn}
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? t('All events') : f}
            </button>
          ))}
          <button type="button" className={styles.pauseBtn} aria-pressed={paused} onClick={() => setPaused((p) => !p)}>
            <i>{paused ? '▶' : '▮▮'}</i>
            {paused ? t('Resume') : t('Pause')}
          </button>
        </div>

        <div className={styles.body}>
          {visibleRows.map((row) => {
            const open = openId === row.id;
            return (
              <div
                key={row.id}
                className={cn(
                  styles.row,
                  row.event.hi && styles.rowHi,
                  row.entering && styles.rowEnter,
                  open && styles.rowOpen,
                )}
                role="button"
                tabIndex={0}
                onClick={() => toggleRow(row.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleRow(row.id);
                  }
                }}
              >
                <div className={styles.main}>
                  <span className={styles.sev} />
                  <span className={styles.kind}>{row.event.k}</span>
                  <span className={styles.headline}>
                    <span className={styles.hl}>{row.event.b}</span>
                    <span className={styles.ent}>{row.event.e}</span>
                  </span>
                  <span className={styles.source}>
                    <Flag code={row.event.flag} className={styles.flag} />
                    {row.event.s}
                  </span>
                  <span className={styles.agoLabel} suppressHydrationWarning>
                    {ago(Date.now() - row.born)}
                  </span>
                </div>

                {open && (
                  <div className={styles.det}>
                    <div className={styles.detIn}>
                      <div>
                        <span>{t('Filing')}</span>
                        <b>{row.event.r}</b>
                      </div>
                      <div>
                        <span>{t('Registry identifier')}</span>
                        <b>{row.event.n}</b>
                      </div>
                      <div>
                        <span>{t('Retrieved')}</span>
                        <b>{new Date(row.born).toISOString().slice(0, 19).replace('T', ' ')} UTC</b>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.foot}>
          <span>SAMPLE STREAM</span>
          <span className={styles.hint}>{t('Select a row to see the filing behind it')}</span>
        </div>
      </div>

      <div className={styles.cta}>
        <Button type="blue" size="xl" href="/continuous-monitoring">
          {t('See how monitoring works')}
        </Button>
      </div>
    </Section>
  );
};
